import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import yup from '../../../../utility/yup';
import useStyles from './styles';
import { useActionDispatcher } from '../../../../hooks';
import { createVideoEditOrderRequest, resetCurrentOrder } from '../../../../store/modules/orders/actions';
import { isOrderPersistent, selectOrder } from '../../../../store/modules/orders/selectors';
import { selectProject } from '../../../../store/modules/projects/selectors';
import { VideoOrderForm } from '../../../../store/modules/orders/types';
import { ReactHookFormStepModal } from '../../../../components';
import StepOneHookForm from './StepOne';
import StepTwo from './StepTwo';
import { getAllQueryData } from '../../../../utility/helpers';

type Props = {
  open: boolean;
  close: () => void;
};

const orderSchema = yup.object({
  options: yup.object({
    order_title: yup.lazy((value) => (!value ? yup.string()
      : yup.string().matches(/^[\p{Alpha}\s+(.\-`'&)]+$/u, 'Title has a wrong symbols'))),
    format: yup.object({
      standard: yup.boolean(),
      square: yup.boolean(),
      portrait: yup.boolean(),
    }),
  }),
  scene_id: yup.number().min(1, 'Script is required').label('Script'),
}).test(
  'options.format',
  '',
  (obj) => {
    if (obj.options.format.standard || obj.options.format.square || obj.options.format.portrait) {
      return true;
    }

    throw new yup.ValidationError(
      'You must choose format',
      null,
      'options.format.standard',
    );
  },
);

const VideoEditStepModal = ({ open, close }: Props) => {
  const [title, setTitle] = useState('New Video Edit Order');
  const classes = useStyles();
  const { data: currentProject } = useSelector(selectProject);
  const createVideoEditOrder = useActionDispatcher(createVideoEditOrderRequest);
  const persistent = useSelector(isOrderPersistent);
  const { loading } = useSelector(selectOrder);
  const queryParams = getAllQueryData();
  const { scriptId } = queryParams;
  const dispatch = useDispatch();

  const initialValues: VideoOrderForm = useMemo(() => ({
    title: '',
    type_id: 'video_edit',
    status_id: 'created',
    video_id: -1,
    project_id: currentProject?.id || -1,
    scene_id: scriptId ? parseInt(scriptId, 10) : -1,
    user_id: currentProject?.ref.user_owner.id || -1,
    organization_id: currentProject?.organization_id || -1,
    assigned_to_id: -1,
    first_edit_delivery_id: 'overnight',
    options: {
      // step 1
      order_title: '',
      scene_title: '',
      format: {
        standard: true,
        square: false,
        portrait: false,
      },
      closed_captions: 'sub_in_video',
      // step 2
      video_duration_key: 1,
      additional_min: 6,
      total_price: 0,
    },
  }), [currentProject, scriptId]);

  const steps = [
    { title: 'Step 1', content: <StepOneHookForm setModalTitle={setTitle} /> },
    { title: 'Step 2', content: <StepTwo setModalTitle={setTitle} /> },
  ];


  /* eslint react-hooks/exhaustive-deps: 0 */
  useEffect(() => {
    if (persistent) {
      close();
    }

    return () => {
      dispatch(resetCurrentOrder());
      close();
    };
  }, [persistent]);

  return (
    <>
      {open && (
        <ReactHookFormStepModal
          open={open}
          onCloseHandler={close}
          title={title}
          formSettings={{
            initialValues,
            onSubmit: createVideoEditOrder,
            validationSchema: orderSchema,
            submitTitle: 'Start Order',
            loading,
            hideCancel: true,
          }}
          paperClassName={classes.stepModalPaper}
          // dialogClassName={classes.stepModalDialogContent}
          steps={steps}
          nextStepValidFields={['options.format', 'scene_id', 'options.order_title']}
        />
      )}
    </>
  );
};

export default VideoEditStepModal;
